import { AuthError, type AuthSession } from './types';

/**
 * Reads a JWT's expiry without verifying it. Verification is the API's job; the browser only needs to know whether
 * the token is shaped like a JWT and still in date, so it can ask for a new one rather than send a dead one.
 */
export function readSession(token: string, now: number = Date.now()): AuthSession {
  const trimmed = token.trim();
  const parts = trimmed.split('.');
  if (parts.length !== 3 || parts.some((part) => part.length === 0)) {
    throw new AuthError('The token is not a JSON Web Token.', 'malformed');
  }
  let payload: unknown;
  try {
    payload = JSON.parse(decodeBase64Url(parts[1] ?? ''));
  } catch {
    throw new AuthError('The token payload could not be read.', 'malformed');
  }
  const exp =
    typeof payload === 'object' && payload !== null && 'exp' in payload && typeof payload.exp === 'number'
      ? payload.exp * 1000
      : null;
  if (exp !== null && exp <= now) throw new AuthError('The token has expired.', 'expired');
  return { accessToken: trimmed, expiresAt: exp };
}

function decodeBase64Url(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}
